/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { getGalileoAb } from '@/shared/galileoAb';
import { ABResEnum } from '@/enums/abExperiment';
import { ubtSendError } from '@/shared/utils/ubt';
import platform, { appVersion } from '@/shared/utils/platform';
import { versionCompare } from '@/shared/utils/versionCompare';
import { getWisdomConfig } from '@/shared/wisdom';
import { checkContactPermission } from '@/api/native/contactImport';
import { ContactPermissionStatusEnum } from '@/api/native/types';
import store, { updateModuleState } from '@/store';
import { ubtSetupModuleExpose } from '@/shared/utils/ubt/ubt-setup';
import { TP_CONTACT_IMPORT_ICON } from './tianqi';

/** 仅在哈啰app内且不包括鸿蒙系统，以及版本为6.98.70及以上，才允许导入联系人 */
export const isSupportedEnvironment = (): boolean => platform.isEasyBike && !platform.isHarmony && versionCompare(appVersion, '6.98.70') >= 0;

/** 判断开关 */
export const isContactImportEnabled = async (): Promise<boolean> => {
  const isEnabled = await getWisdomConfig<boolean>('contactImportSwitch');
  return Boolean(isEnabled);
};

/** 判断权限 */
export const ishasContactPermission = async (): Promise<boolean> => {
  const permissionStatus = await checkContactPermission();
  return permissionStatus === ContactPermissionStatusEnum.AUTHORIZED;
};

/*
 * 联系人导入AB分流测试管理类
 * 用于管理联系人导入功能的AB实验分流逻辑
 */
class ContactImportAb {
  private isExperimentContact = false;

  /*
   * 初始化AB分流实验，获取并缓存实验结果
   * 实验地址:
   */
  public async initContactImportExperimentResult(): Promise<void> {
    try {
      const res = await getGalileoAb().getAbResCatchError('202601121111480947', '202601121111481045');
      this.isExperimentContact = res?.result === ABResEnum.HINT;
    } catch (error) {
      ubtSendError(error as Error);
    }
  }

  /*
   * 读取缓存的AB测试结果
   */
  public getCachedResult(): boolean {
    return this.isExperimentContact;
  }
}

const contractImportAb = new ContactImportAb();

/**
 * 从 store 中获取缓存的联系人导入图标显示状态
 */
export function getCachedShouldShowContactImportIcon(): boolean | undefined {
  return store.state.verifyCredit?.shouldShowContactImportIcon;
}

export function getCachedContactImportAuthType(): number | undefined {
  return store.state.verifyCredit?.contactImportAuthType;
}

/**
 * 判断是否应该显示联系人导入图标
 * 流程：版本前置 → wisdom开关 → 判断权限 → 实验分组 → 展示
 */
export const shouldShowContactImportIcon = async (): Promise<boolean> => {
  // 先检查 store 中是否有缓存结果
  const cachedResult = getCachedShouldShowContactImportIcon();
  if (typeof cachedResult === 'boolean') {
    ubtSetupModuleExpose(TP_CONTACT_IMPORT_ICON,
      { authType: getCachedContactImportAuthType() });
    return cachedResult;
  }

  let finalResult = false;
  let permissionStatus: number | undefined;

  if (isSupportedEnvironment()) {
    const isEnabled = await isContactImportEnabled();
    // 获取权限状态：0-未知, 1-未决定, 2-已授权, 3-已拒绝, 4-受限制
    permissionStatus = await checkContactPermission();
    const hasContactPermission = permissionStatus === ContactPermissionStatusEnum.AUTHORIZED;
    // 首次上报
    ubtSetupModuleExpose(TP_CONTACT_IMPORT_ICON, { authType: permissionStatus });

    if (isEnabled) {
      // 权限是 0（未知）
      if (permissionStatus === ContactPermissionStatusEnum.UNKNOWN) {
        finalResult = false;
      } else if (hasContactPermission) {
        // 权限是 2（已授权）时，显示图标
        finalResult = true;
      } else {
        // 权限是 1（未决定）或 3（已拒绝）时，进入实验分组逻辑
        await contractImportAb.initContactImportExperimentResult();
        const isHitExperiment = contractImportAb.getCachedResult();
        finalResult = !!isHitExperiment;
      }
    }
  }

  await updateModuleState('verifyCredit', {
    shouldShowContactImportIcon: finalResult,
    contactImportAuthType: permissionStatus,
  });
  return finalResult;
};
